// ink-component
import React, { ReactNode } from 'react';

import styled from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import { headerTokens } from '../../tokens/components/Header/tokens';

export interface HeaderProps {
  /** Inner content */
  children?: ReactNode;
  /** Custom brand logo */
  logo?: ReactNode;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const StyledHeader = styled.header`
  align-items: center;
  background-color: ${headerTokens.BackgroundColorBaseDefault};
  border-bottom: 1px solid ${headerTokens.BorderColorBottomDefault};
  display: flex;
  height: ${headerTokens.SizeBaseDefault};
  padding: 6px 18px;
  width: 100%;
`;

const StyledHeaderLogo = styled.div`
  display: flex;
  padding-right: 20px;
`;

const StyledHeaderControls = styled.div`
  width: 100%;
`;

const defaultLogo = (
  <svg xmlns="http://www.w3.org/2000/svg" width="65" height="28" fill="none" viewBox="0 0 65 28">
    <title>Carta logo</title>
    <g fill="#000" clipPath="url(#clip0_3002_38424)">
      <path d="M1.717 1.706h61.182v24.588H1.717V1.706zM0 28h64.615V0H0v28z" />
      <path d="M14.329 19.69c-2.425 0-4.982-1.863-4.982-4.993 0-3.168 2.677-5.027 5.077-5.027 1.719 0 3.321.645 4.181 2.128l-1.604.934a3.015 3.015 0 00-1.1-.992 3.037 3.037 0 00-1.442-.352c-1.413 0-3.091 1.101-3.091 3.283 0 2.128 1.606 3.301 3.225 3.301 1.128 0 2.063-.627 2.617-1.596l1.642.758c-.93 1.67-2.59 2.56-4.517 2.56M24.773 17.887c1.835 0 3.091-1.365 3.091-3.207 0-1.841-1.256-3.205-3.091-3.205-1.835 0-3.13 1.344-3.13 3.168 0 1.86 1.26 3.244 3.13 3.244zm-.193 1.803c-2.898 0-4.963-2.182-4.963-5.01 0-2.88 2.043-5.007 4.963-5.007 1.339 0 2.628.561 3.246 1.423V9.92h1.985v9.505h-1.983v-1.176c-.65.853-1.91 1.44-3.246 1.44M39.829 19.419v-7.663h-1.526V9.858h1.545V7.05h2.043v2.808h2.118v1.898H41.89v7.663H39.83zM49.855 17.887c1.83 0 3.091-1.365 3.091-3.207 0-1.841-1.261-3.205-3.091-3.205-1.83 0-3.13 1.344-3.13 3.168 0 1.86 1.255 3.244 3.13 3.244zm-.193 1.803c-2.898 0-4.962-2.182-4.962-5.01 0-2.88 2.043-5.007 4.962-5.007 1.339 0 2.63.561 3.246 1.423V9.92h1.986v9.505h-1.986v-1.176c-.65.853-1.91 1.44-3.246 1.44M31.854 19.41V9.906h1.888v1.65c.391-1.072 1.217-1.802 2.312-1.802.237-.004.475.022.706.076v1.84c-1.546-.18-2.844.891-2.844 3.338v4.402h-2.062z" />
    </g>
    <defs>
      <clipPath id="clip0_3002_38424">
        <path fill="#000" d="M0 0H64.615V28H0z" />
      </clipPath>
    </defs>
  </svg>
);

const globalProps = getGlobalProps({});

/**
 * @purpose Displays an application's main navigation and actions
 * @categories navigation
 * @aka top bar, primary navigation
 */
function Header({ children, logo, ...otherProps }: HeaderProps) {
  return (
    <StyledHeader {...globalProps} data-testid={otherProps['data-testid']}>
      {logo !== null && <StyledHeaderLogo>{logo}</StyledHeaderLogo>}
      <StyledHeaderControls>{children}</StyledHeaderControls>
    </StyledHeader>
  );
}

Header.defaultProps = {
  children: null,
  logo: defaultLogo,
  'data-testid': undefined,
};

Header.displayName = 'Header';

export default Header;
